import React from 'react'
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import AdminAdSenseBanner from './components/AdminAdSenseBanner'
import SidebarAdSense from './components/SidebarAdSense'
import PrivateRoute from './components/PrivateRoute'
import AdminRoute from './components/AdminRoute'
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import Tours from './pages/Tours'
import Services from './pages/Services'
import TourPlanner from './pages/TourPlanner'
import Profile from './pages/Profile'
import Bookings from './pages/Bookings'
import AdminDashboard from './pages/AdminDashboard'
import Contact from './pages/Contact'
import TourDetails from './pages/TourDetails'
import TourBooking from './pages/TourBooking'
import ServiceBooking from './pages/ServiceBooking'
import ServiceDetails from './pages/ServiceDetails'
import MyCustomTrips from './pages/MyCustomTrips'
import MyEnquiries from './pages/MyEnquiries'
import AboutUs from './pages/AboutUs'
import Adventures from './pages/Adventures'
import Certifications from './pages/Certifications'
import TermsAndConditions from './pages/TermsAndConditions'
import CancellationPolicy from './pages/CancellationPolicy'
import { organizationSchema, websiteSchema } from './utils/structuredData'
import { trackPageView, trackLogin } from './utils/analytics'

// Track page views on route changes
function RouteTracker() {
  const location = useLocation()
  React.useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location])
  return null
}

// Scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation()
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// Placeholder components for routes not yet created
const AuthCallback = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { loginWithToken } = useAuth()
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    const token = params.get('token')
    const authError = params.get('error')

    if (authError) {
      setError('Authentication failed. Please try again.')
      setTimeout(() => navigate('/login'), 2000)
      return
    }

    if (!token) {
      setError('No authentication token received.')
      setTimeout(() => navigate('/login'), 2000)
      return
    }

    loginWithToken(token)
      .then(() => {
        trackLogin('oauth')
        navigate('/')
      })
      .catch(() => {
        setError('Authentication failed. Please try again.')
        setTimeout(() => navigate('/login'), 2000)
      })
  }, [])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <h1 className="text-2xl font-bold">Processing Authentication...</h1>
    </div>
  )
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <RouteTracker />
          <ScrollToTop />
          {/* Site-wide structured data */}
          <Helmet>
            <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
          </Helmet>
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/tours" element={<Tours />} />
                <Route path="/tours/:id/book" element={<TourBooking />} />
                <Route path="/tours/:id" element={<TourDetails />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:id" element={<ServiceDetails />} />
                <Route path="/services/:id/book" element={<ServiceBooking />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/adventures" element={<Adventures />} />
                <Route path="/certifications" element={<Certifications />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/tour-planner" element={<TourPlanner />} />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/bookings"
                  element={
                    <PrivateRoute>
                      <Bookings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/my-custom-trips"
                  element={
                    <PrivateRoute>
                      <MyCustomTrips />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/my-enquiries"
                  element={
                    <PrivateRoute>
                      <MyEnquiries />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
              </Routes>
            </main>
            <AdminAdSenseBanner />
            <Footer />
            <SidebarAdSense />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  )
}

export default App
